import { Alert, CircularProgress, Grid2, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";

import useRequireCurrentProject from "../../hooks/useRequireCurrentProject";
import { ProjectActiveProtocolOverviewCard } from "./components/ProjectActiveProtocolOverviewCard";
import { ProjectRecentVisitsCard } from "./components/ProjectRecentVisitsCard";
import { ProjectStatisticCard } from "./components/ProjectStatisticCard";
import { ProjectUpcomingVisitsCard } from "./components/ProjectUpcomingVisitsCard";
import { ProjectVisitStatusDistributionCard } from "./components/ProjectVisitStatusDistributionCard";
import { useProjectStatisticsGetById } from "./useProjectStatisticsGetById";

export function ProjectOverviewScreen() {
    const { t } = useTranslation("project");

    const { project } = useRequireCurrentProject();
    const { data, loading, error } = useProjectStatisticsGetById(project.id);
    const values = data?.projectStatisticsGetById;

    if (loading) {
        return (
            <GaiaPageLayoutColumn maxWidth="xl">
                <Stack alignItems="center" py={8}>
                    <CircularProgress />
                </Stack>
            </GaiaPageLayoutColumn>
        );
    }

    if (error || values === null || values === undefined) {
        return (
            <GaiaPageLayoutColumn maxWidth="xl">
                <Stack alignItems="center" py={8}>
                    <Alert severity="error">{error?.message}</Alert>
                </Stack>
            </GaiaPageLayoutColumn>
        );
    }

    return (
        <GaiaPageLayoutColumn maxWidth="xl">
            <Typography variant="h1">{t("overviewScreen.title")}</Typography>

            <Grid2
                container
                mb={1}
                spacing={2}
                sx={{
                    "& .MuiPaper-root": {
                        height: "100%",
                    },
                }}
            >
                <Grid2 size={STATISTICS_GRID_SIZE}>
                    <ProjectStatisticCard
                        href="planned"
                        title={t("overviewScreen.visitProgress.title")}
                        mainValue={`${values.completedVisitsPercentage.toFixed(0)}%`}
                        subtitle={t("overviewScreen.visitProgress.subtitle", {
                            completed: values.completedNumberOfVisits,
                            total: values.totalNumberOfVisits,
                        })}
                    />
                </Grid2>
                <Grid2 size={STATISTICS_GRID_SIZE}>
                    <ProjectStatisticCard
                        href="protocols"
                        title={t("overviewScreen.activeProtocols.title")}
                        mainValue={values.numberOfActiveProtocols}
                        subtitle={t("overviewScreen.activeProtocols.subtitle", {
                            active: values.numberOfActiveProtocols,
                            total: values.totalNumberOfProtocols,
                        })}
                    />
                </Grid2>
                <Grid2 size={STATISTICS_GRID_SIZE}>
                    <ProjectStatisticCard
                        title={t("overviewScreen.areas.title")}
                        mainValue={values.totalNumberOfAreas}
                        subtitle={t("overviewScreen.areas.subtitle")}
                    />
                </Grid2>
                <Grid2 size={STATISTICS_GRID_SIZE}>
                    <ProjectStatisticCard
                        title={t("overviewScreen.functionsFound.title")}
                        mainValue={values.totalNumberOfFunctions}
                        subtitle={t("overviewScreen.functionsFound.subtitle", {
                            recent: values.numberOfRecentlyFoundFunctions,
                        })}
                    />
                </Grid2>
                <Grid2 size={STATISTICS_GRID_SIZE}>
                    <ProjectStatisticCard
                        href="planned?plannerVisitStatus=approved"
                        title={t("overviewScreen.reports.title")}
                        mainValue={values.completedNumberOfVisits}
                        subtitle={t("overviewScreen.reports.subtitle")}
                    />
                </Grid2>
                <Grid2 size={TABLE_GRID_SIZE}>
                    <ProjectVisitStatusDistributionCard distribution={values.visitStatusDistribution} />
                </Grid2>
                <Grid2 size={TABLE_GRID_SIZE}>
                    <ProjectActiveProtocolOverviewCard protocols={values.activeProtocols} />
                </Grid2>
                <Grid2 size={TABLE_GRID_SIZE}>
                    <ProjectUpcomingVisitsCard visits={values.upcomingPlannerVisits} />
                </Grid2>
                <Grid2 size={TABLE_GRID_SIZE}>
                    <ProjectRecentVisitsCard visits={values.recentlyApprovedPlannerVisits} />
                </Grid2>
            </Grid2>
        </GaiaPageLayoutColumn>
    );
}

const STATISTICS_GRID_SIZE = { xs: 12, sm: 6, md: 12 / 5 };
const TABLE_GRID_SIZE = { xs: 12, md: 6 };
